<template>
  <div class="space-y-4">
    <div>
      <h2>{{ t('inspectionShots') }}</h2>
      <MultiSelectField
        name="requireInspectionStationIds"
        :label="t('requireInspectionStations')"
        value-prop="value"
        mode="tags"
        :options="stationOptions"
        class="w-full"
      />
      <p class="mt-1 text-sm text-obsidian-600">{{ t('requireInspectionStationsDescription') }}</p>
    </div>
    <p class="text-sm text-obsidian-600">{{ t('inspectionShotsDescription') }}</p>
    <div class="grid gap-x-4 sm:grid-cols-2 md:grid-cols-3">
      <div v-for="shotKey in catalog" :key="shotKey" class="flex h-8 items-center">
        <Checkbox
          :model-value="isEnabled(shotKey)"
          :label="getShotLabel(shotKey)"
          :disabled="isLastEnabled(shotKey)"
          @update:model-value="(enabled) => handleToggle(shotKey, enabled)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import Checkbox from '@/components/Checkbox.vue';
import MultiSelectField from '@/components/MultiSelectField.vue';
import { useShotLabel } from '@/components/inspections/use-shot-label';
import { InspectionShotKey } from '@carvia/ros-client-types';
import { useStationsWithQuery } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { getLocalizedField } from '@/utils/get-localized-field';

const { t, locale } = useI18n();
const { getShotLabel } = useShotLabel();

const { value } = useField<InspectionShotKey[] | undefined>('disabledInspectionShotKeys');

/** Enum order is the catalog order. */
const catalog = Object.values(InspectionShotKey);
const disabledShotKeys = computed(() => new Set(value.value ?? []));
const enabledCount = computed(
  () => catalog.filter((key) => !disabledShotKeys.value.has(key)).length,
);

const isEnabled = (shotKey: InspectionShotKey) => !disabledShotKeys.value.has(shotKey);

/** The last enabled shot stays on; an inspection without shots would be pointless. */
const isLastEnabled = (shotKey: InspectionShotKey) =>
  isEnabled(shotKey) && enabledCount.value === 1;

const handleToggle = (shotKey: InspectionShotKey, enabled: boolean) => {
  const disabled = new Set(disabledShotKeys.value);
  if (enabled) disabled.delete(shotKey);
  else disabled.add(shotKey);
  value.value = catalog.filter((key) => disabled.has(key));
};

const stationsResult = useStationsWithQuery({ limit: 500 });
const stations = useFlattenPaginatedData(stationsResult.data);
const stationOptions = computed(
  () =>
    stations.value?.map((station) => ({
      label: getLocalizedField(station.info, locale.value),
      value: station.id,
    })) ?? [],
);
</script>

<i18n lang="json">
{
  "en": {
    "inspectionShots": "Vehicle photos",
    "requireInspectionStations": "Require vehicle photos on short-term handover at",
    "requireInspectionStationsDescription": "Long-term handovers always include this step",
    "inspectionShotsDescription": "Photos asked for at handover and return. Changes apply to handovers started from now on; a return always asks for the photos of its handover."
  },
  "de": {
    "inspectionShots": "Fahrzeugfotos",
    "requireInspectionStations": "Fahrzeugfotos bei Kurzzeit-Übergabe verlangen an",
    "requireInspectionStationsDescription": "Bei Langzeitmieten ist dieser Schritt immer enthalten",
    "inspectionShotsDescription": "Fotos, die bei Übergabe und Rückgabe abgefragt werden. Änderungen gelten für Übergaben, die ab jetzt begonnen werden; eine Rückgabe fragt immer die Fotos ihrer Übergabe ab."
  },
  "es": {
    "inspectionShots": "Fotos del vehículo",
    "requireInspectionStations": "Exigir fotos del vehículo en la entrega de corta duración en",
    "requireInspectionStationsDescription": "Las entregas de larga duración siempre incluyen este paso",
    "inspectionShotsDescription": "Fotos que se piden en la entrega y la devolución. Los cambios se aplican a las entregas que se inicien a partir de ahora; una devolución siempre pide las fotos de su entrega."
  }
}
</i18n>
